import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Checkbox,
  cn,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  DialogTitle,
  Spinner,
  Stack,
  TextInput,
  toast,
} from 'component-library';
import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RestApiBadRequestServerError } from '@/api/rest/resources/errors/RestApiBadRequestError';
import { IndicatorFactType } from '@/api/rest/resources/types/fact';
import {
  PlotGroupComparison,
  PostOrganizationPlotGroupComparisonsRequestBodyData,
} from '@/api/rest/resources/types/organization';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  SuspenseBoundary,
  useFormField,
} from '@/components';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { Logger } from '@/lib/logs/logger';
import { isNullish } from '@/utils/booleans';

import { EditComparisonSubmitProps, useEditComparison } from '../../hooks/usePlotGroupComparisons';
import { ALL_INDICATOR_FACT_TYPES, availableGraphsToIndicatorNames } from '../constants';

export const EditPlotGroupComparison = ({
  organizationId,
  plotGroupComparison,
  selectedIndicators,
  onIndicatorsChange,
  disabled = false,
}: {
  organizationId: string;
  plotGroupComparison: PlotGroupComparison;
  selectedIndicators?: IndicatorFactType[];
  onIndicatorsChange?: (indicators: IndicatorFactType[]) => void;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  const [isEditingComparison, setIsEditingComparison] = useState(false);
  const disable = disabled ?? isNullish(plotGroupComparison);
  return (
    <Stack className='flex flex-row justify-end'>
      <Button
        variant='outline'
        onClick={() => setIsEditingComparison(true)}
        disabled={disable}
        leftAdornment={
          <Icon
            icon='ri-layout-column-line'
            className={cn('w-4 p-0 m-0', disable ? 'text-text-disabled' : 'text-primary-100')}
          />
        }
      >
        {t('global.compare.editPlotGroupComparison.buttonLabel')}
      </Button>
      <EditComparison
        organizationId={organizationId}
        plotGroupComparison={plotGroupComparison}
        selectedIndicators={selectedIndicators}
        onIndicatorsChange={onIndicatorsChange}
        open={isEditingComparison}
        onOpenChange={(open) => setIsEditingComparison(open)}
      />
    </Stack>
  );
};

export type EditComparisonProps = {
  organizationId: string;
  plotGroupComparison: PlotGroupComparison;
  selectedIndicators?: IndicatorFactType[];
  onIndicatorsChange?: (indicators: IndicatorFactType[]) => void;
  open: boolean;
  onOpenChange: DialogProps['onOpenChange'];
};

export const EditComparison = ({
  organizationId,
  plotGroupComparison,
  selectedIndicators,
  onIndicatorsChange,
  open,
  onOpenChange,
}: EditComparisonProps) => {
  const { t } = useTranslation();
  const { submit, isSubmitting } = useEditComparison();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid='edit-plot-group-comparison'>
        <DialogHeader>
          <DialogTitle>{t('global.compare.editPlotGroupComparison.buttonLabel')}</DialogTitle>
        </DialogHeader>
        <SuspenseBoundary
          fallback={
            <Stack centerMain>
              <Spinner size={10} />
            </Stack>
          }
        >
          <Form
            onSuccess={() => onOpenChange?.(false)}
            plotGroupComparison={plotGroupComparison}
            selectedIndicators={selectedIndicators}
            onIndicatorsChange={onIndicatorsChange}
            organizationId={organizationId}
            submit={submit}
            isSubmitting={isSubmitting}
            open={open}
          />
        </SuspenseBoundary>
      </DialogContent>
    </Dialog>
  );
};

export type EditComparisonFields = Partial<PostOrganizationPlotGroupComparisonsRequestBodyData>;

const Form = ({
  submit,
  isSubmitting,
  onSuccess,
  plotGroupComparison,
  selectedIndicators,
  onIndicatorsChange,
  organizationId,
  open,
}: {
  submit: (props: EditComparisonSubmitProps) => void;
  isSubmitting: boolean;
  onSuccess: () => void;
  plotGroupComparison: PlotGroupComparison;
  selectedIndicators?: IndicatorFactType[];
  onIndicatorsChange?: (indicators: IndicatorFactType[]) => void;
  organizationId: string;
  open: boolean;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const defaultIndicators = plotGroupComparison.indicators ?? ALL_INDICATOR_FACT_TYPES;
  const indicatorsToUse = selectedIndicators ?? defaultIndicators;

  const form = useForm<EditComparisonFields>({
    defaultValues: {
      name: plotGroupComparison.name ?? '',
      indicators: indicatorsToUse,
    },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({
      name: plotGroupComparison.name ?? '',
      indicators: indicatorsToUse,
    });
  }, [form, indicatorsToUse, open, plotGroupComparison.name]);

  const selectedIndicatorsInForm = form.watch('indicators') ?? ALL_INDICATOR_FACT_TYPES;

  const onIndicatorChange = (factType: IndicatorFactType, checked: boolean) => {
    const current = form.getValues('indicators') ?? ALL_INDICATOR_FACT_TYPES;
    const updated = checked ? [...current, factType] : current.filter((indicator) => indicator !== factType);
    form.setValue('indicators', updated, { shouldDirty: true });
  };

  const isFormPristine = !form.formState.isDirty;
  const isRequiredLabel = t('global.ui.form.input.required');

  const onSubmit: SubmitHandler<EditComparisonFields> = async (inputs) => {
    try {
      form.clearErrors();
      const plotGroupIds = plotGroupComparison ? plotGroupComparison.plot_groups?.map((group) => group.id) : [];
      submit({
        id: plotGroupComparison.id ?? null,
        name: inputs.name ?? plotGroupComparison.name,
        organizationId,
        plotGroupIds,
        indicators: inputs.indicators ?? plotGroupComparison.indicators,
        onSuccess: () =>
          queryClient.resetQueries({ queryKey: ['organization', organizationId, 'plot-group-comparisons'] }),
      } as EditComparisonSubmitProps);

      onIndicatorsChange?.((inputs.indicators as IndicatorFactType[]) ?? indicatorsToUse);

      onSuccess();
      form.reset();
    } catch (error) {
      if (error instanceof Error) {
        Logger.error(error);
      }

      if (error instanceof RestApiBadRequestServerError && error.formErrors.length > 0) {
        error.formErrors.forEach((formError) => {
          form.setError(formError.field as keyof EditComparisonFields, {
            type: 'custom',
            message: formError.messages?.[0],
          });
        });
      } else {
        toast({
          title: t('global.ui.toast.errorToastFallbackTitle'),
          description: t('global.ui.toast.errorToastFallbackDescription'),
          type: 'error',
        });
      }
    }
  };

  return (
    <FormProvider {...form}>
      <DialogBody>
        <form
          id='edit-plot-group-comparison-form'
          onSubmit={form.handleSubmit(onSubmit)}
          data-testid='edit-plot-group-comparison-form'
        >
          <fieldset disabled={isSubmitting}>
            <Stack spacing={8}>
              <FormField
                control={form.control}
                name='name'
                rules={{
                  required: isRequiredLabel,
                }}
                render={({ field: { value, ...field } }) => (
                  <FormItem className='flex flex-col gap-1'>
                    <CustomFormLabel>{t('global.compare.editPlotGroupComparison.form.name.label')}*</CustomFormLabel>
                    <FormControl>
                      <TextInput
                        value={value}
                        placeholder={t('global.compare.editPlotGroupComparison.form.name.placeholder')}
                        {...field}
                      />
                    </FormControl>
                    <CustomFormMessage>{form.formState.errors.name?.message}</CustomFormMessage>
                    <FormDescription>{t('global.compare.editPlotGroupComparison.form.name.helper')}</FormDescription>
                  </FormItem>
                )}
              />
              <FormItem className='flex flex-col gap-2'>
                <CustomFormLabel>{t('global.compare.editPlotGroupComparison.form.indicators.label')}</CustomFormLabel>
                {Object.entries(availableGraphsToIndicatorNames).map(([graphType, factType]) => (
                  <Checkbox
                    key={graphType}
                    id={graphType}
                    checked={selectedIndicatorsInForm.includes(factType as IndicatorFactType)}
                    onCheckedChange={(checked) => onIndicatorChange(factType as IndicatorFactType, Boolean(checked))}
                  >
                    <FormLabel htmlFor={graphType}>
                      {t(
                        `global.compare.graphs.indicators.${graphType as keyof typeof availableGraphsToIndicatorNames}`,
                      )}
                    </FormLabel>
                  </Checkbox>
                ))}
                <FormDescription>{t('global.compare.editPlotGroupComparison.form.indicators.helper')}</FormDescription>
              </FormItem>
            </Stack>
          </fieldset>
        </form>
      </DialogBody>
      <DialogFooter className='mt-12'>
        <DialogClose asChild>
          <Button variant='text' size='small' disabled={isSubmitting}>
            {t('global.ui.buttons.cancel')}
          </Button>
        </DialogClose>
        <Button
          form='edit-plot-group-comparison-form'
          size='small'
          type='submit'
          disabled={isSubmitting || isFormPristine}
          loading={isSubmitting}
        >
          {t('global.ui.buttons.save')}
        </Button>
      </DialogFooter>
    </FormProvider>
  );
};

const CustomFormLabel: FC<PropsWithChildren> = ({ children }) => {
  const { error } = useFormField();

  return (
    <FormLabel
      className={cn('text-[.75rem] font-medium leading-4 text-text-secondary md:text-[.8125rem] md:leading-5', {
        'text-error': error,
      })}
    >
      {children}
    </FormLabel>
  );
};

const CustomFormMessage: FC<PropsWithChildren> = ({ children }) => (
  <FormMessage className='typography-inputLabel mt-0 text-error'>{children}</FormMessage>
);
